import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, PartyPopper, Trophy, Megaphone } from "lucide-react";
import { API } from "@/constants/site";
import { Stagger, StaggerItem } from "@/components/site/Reveal";

const CATEGORY_STYLE = {
  Celebration: { chip: "bg-brand-coral/15 text-brand-deepcoral", icon: PartyPopper },
  Achievement: { chip: "bg-brand-sun/25 text-amber-700", icon: Trophy },
  Announcement: { chip: "bg-brand-sky/15 text-brand-deepsky", icon: Megaphone },
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export const NewsSection = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/news`).then(({ data }) => setPosts(data)).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section data-testid="news-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
      <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-3">News & Events</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">What's happening at school</h2>
        </div>
        <p className="font-hand text-2xl text-brand-deepcoral -rotate-2">celebrations, wins & announcements</p>
      </div>
      <Stagger className="grid gap-8 md:grid-cols-3">
        {posts.slice(0, 6).map((p) => {
          const style = CATEGORY_STYLE[p.category] || CATEGORY_STYLE.Announcement;
          const Icon = style.icon;
          return (
            <StaggerItem key={p.id}>
              <article data-testid={`news-card-${p.id}`} className="group h-full overflow-hidden rounded-[2.5rem] border border-amber-200/50 bg-white shadow-[0_15px_40px_rgba(2,132,199,0.08)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(251,113,133,0.15)]">
                {p.image && (
                  <div className="overflow-hidden">
                    <img src={p.image} alt={p.title} className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                )}
                <div className="p-8">
                <div className="flex items-center justify-between gap-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold ${style.chip}`}>
                    <Icon className="h-3.5 w-3.5" /> {p.category}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                    <CalendarDays className="h-3.5 w-3.5" /> {formatDate(p.date || p.created_at)}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{p.body}</p>
                {p.link && (
                  <a href={p.link} target="_blank" rel="noreferrer" data-testid={`news-read-more-${p.id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-deepcoral hover:underline">
                    Read full story →
                  </a>
                )}
                </div>
              </article>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
};
